import { DocumentsClient, RapportClient, DossierClient, QuestionnaireAtelier } from "./PublicServices";
import { CourriersFamille } from "./CourriersFamille";
import SignaturePage from "./SignaturePage";
import { fmtEur } from "./shared";
import PaiementPage from "./PaiementPage";
// AUPRÉA — Page publique client /p/{token} (aucun login requis)
import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND + "/api" });

// ── Palette locale (copie condensée pour éviter dépendance shared.jsx auth) ─
const C = {
  navy: "#1B2A4A", gold: "#C8902C", golddm: "#9A6E1E", goldlt: "#F7EBCF",
  teal: "#1F7A6C", teallt: "#DBEEE9", green: "#166534", greenlt: "#DCFCE7",
  amber: "#B45309", amberlt: "#FEF3C7", red: "#991B1B", redlt: "#FEE2E2",
  gray: "#F7F3EA", gray2: "#FBF9F4", border: "#E2DDD5",
  mid: "#5F5E5A", muted: "#999990", text: "#1B2A4A",
};

const Container = ({ children }) => (
  <div style={{ minHeight: "100vh", background: C.gray, fontFamily: "'Montserrat', -apple-system, sans-serif", padding: "40px 20px" }}>
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
  </div>
);
const Header = ({ conseiller }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40, paddingBottom: 20, borderBottom: `1px solid ${C.border}` }}>
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>AUPRÉA</div>
      <div style={{ fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: ".14em", fontWeight: 700 }}>vous accompagne</div>
    </div>
    {conseiller && (
      <div style={{ textAlign: "right", fontSize: 12, color: C.mid }}>
        <div>Votre consultant</div>
        <div style={{ fontWeight: 700, color: C.navy }}>{[conseiller.prenom, conseiller.nom].filter(Boolean).join(" ").replace(/\b(\w+)\s+\1\b/gi, "$1")}</div>
      </div>
    )}
  </div>
);

// ── Écran principal : la proposition ────────────────────────────────────────
const FormuleCard = ({ nom, formule, badge, selected, onSelect }) => (
  <button onClick={onSelect}
    style={{
      cursor: "pointer", background: "#fff", border: `2px solid ${selected ? C.gold : C.border}`, borderRadius: 14,
      padding: 24, textAlign: "left", fontFamily: "inherit",
      display: "flex", flexDirection: "column", gap: 10,
      boxShadow: selected ? "0 8px 28px rgba(200,144,44,0.28)" : "none",
      position: "relative", minHeight: 320, width: "100%",
    }} data-testid={`p-formule-${nom.toLowerCase()}`}>
    {badge && <div style={{ position: "absolute", top: -10, right: 14, background: C.gold, color: "#fff", padding: "3px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: ".07em" }}>{badge}</div>}
    <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{nom}</div>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
      {(formule.lignes || []).length === 0 ? <em style={{ fontSize: 12, color: C.muted }}>Aucune ligne</em> :
        (formule.lignes || []).map((l, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, color: l.isRemise ? C.green : C.mid }}>
            <span>{l.isRemise ? "− " : ""}{l.nom}{l.isOrientation && <span style={{ fontSize: 10, color: C.teal, marginLeft: 6, fontWeight: 600 }}>· offert</span>}</span>
            <span style={{ fontWeight: 700, color: l.isRemise ? C.green : C.navy, whiteSpace: "nowrap" }}>{l.isOrientation ? "offert" : fmtEur(l.prixTTC)}{l.unite ? ` ${l.unite}` : ""}</span>
          </div>
        ))
      }
    </div>
    <div style={{ borderTop: `.5px solid ${C.border}`, paddingTop: 12, marginTop: 6 }}>
      <div style={{ fontSize: 13, color: C.muted }}>À régler aujourd'hui</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{fmtEur(formule.totalOneShot || 0)}</div>
      {formule.totalMonthly > 0 && <div style={{ fontSize: 13, color: C.mid, marginTop: 3 }}>puis <strong style={{ color: C.navy }}>{fmtEur(formule.totalMonthly)}</strong> / mois</div>}
    </div>
  </button>
);

const PublicClientPage = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [formuleChoisie, setFormuleChoisie] = useState(null);
  const [codePromo, setCodePromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [payMode, setPayMode] = useState("comptant");
  const [processing, setProcessing] = useState(false);
  const [cgvAccept, setCgvAccept] = useState(false);
  const [texts, setTexts] = useState(null);
  const [showModal, setShowModal] = useState(null); // 'cgv' | 'rgpd' | 'mentions' | 'rgpd-demande'

  useEffect(() => {
    api.get(`/public/parcours/${token}`).then(r => {
      setData(r.data);
      setFormuleChoisie(r.data.formule_choisie || "recommande");
      setLoading(false);
    }).catch(e => { setErr(e?.response?.data?.detail || "Impossible de charger votre proposition."); setLoading(false); });
    api.get(`/public/reglages-texts`).then(r => setTexts(r.data)).catch(() => {});
  }, [token]);

  const applyPromo = async () => {
    if (!codePromo.trim()) return;
    try {
      const r = await api.post(`/public/parcours/${token}/promo`, { code: codePromo.trim().toUpperCase() });
      setPromoInfo({ ok: true, conseiller: r.data.conseiller });
    } catch (e) {
      setPromoInfo({ ok: false, msg: e?.response?.data?.detail || "Code invalide" });
    }
  };

  const pay = async () => {
    if (!formuleChoisie) return;
    if (!cgvAccept) { setErr("Veuillez accepter les CGV et la politique de confidentialité pour continuer."); return; }
    setProcessing(true); setErr("");
    try {
      const r = await api.post("/checkout/create", {
        parcoursId: data.id, formule: formuleChoisie,
        origin_url: window.location.origin,
        payment_mode: payMode,
        code_promo: codePromo.trim().toUpperCase() || null,
        cgv_accept: true,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur de paiement"); setProcessing(false);
    }
  };

  if (loading) return <Container><div style={{ textAlign: "center", color: C.mid, marginTop: 100 }}>Chargement…</div></Container>;
  if (err) return <Container><div style={{ background: C.redlt, color: C.red, padding: 20, borderRadius: 10, marginTop: 40 }}>⚠ {err}</div></Container>;

  const formules = data.formules || {};
  const total = formules[formuleChoisie]?.totalOneShot || 0;

  return (
    <Container>
      <img src={`${BACKEND}/api/pixel/${token}`} alt="" width="1" height="1" style={{ position: "absolute", opacity: 0 }} />
      <Header conseiller={data.conseiller} />
      <div style={{ fontSize: 40, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginBottom: 8, lineHeight: 1.1 }}>
        {data.fiche?.prenom || "Bonjour"}, votre proposition personnalisée
      </div>
      <div style={{ fontSize: 16, color: C.mid, marginBottom: 30 }}>Voici les 3 formules que nous avons construites suite à notre échange. Choisissez celle qui vous convient.</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, marginBottom: 30 }}>
        <FormuleCard nom="Essentiel"  formule={formules.essentiel  || {}} selected={formuleChoisie === "essentiel"}  onSelect={() => setFormuleChoisie("essentiel")} />
        <FormuleCard nom="Recommandé" formule={formules.recommande || {}} badge="RECOMMANDÉ" selected={formuleChoisie === "recommande"} onSelect={() => setFormuleChoisie("recommande")} />
        <FormuleCard nom="Complet"    formule={formules.complet    || {}} selected={formuleChoisie === "complet"}    onSelect={() => setFormuleChoisie("complet")} />
      </div>

      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>J'ai un code promo</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input type="text" value={codePromo} onChange={(e) => setCodePromo(e.target.value.toUpperCase())} placeholder="Ex : AUPCH01"
            style={{ padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: "inherit", fontSize: 13, flex: 1, minWidth: 200 }}
            data-testid="p-promo-input" />
          <button onClick={applyPromo} style={{ background: "#fff", border: `1px solid ${C.border}`, padding: "10px 18px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600 }} data-testid="p-promo-apply">Valider</button>
        </div>
        {promoInfo && (
          <div style={{ marginTop: 10, fontSize: 12, color: promoInfo.ok ? C.green : C.red }}>
            {promoInfo.ok ? `✓ Code appliqué — parrainé par ${promoInfo.conseiller}` : `⚠ ${promoInfo.msg}`}
          </div>
        )}
      </div>

      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>Mode de paiement</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, border: `1px solid ${payMode === "comptant" ? C.gold : C.border}`, borderRadius: 10, cursor: "pointer", background: payMode === "comptant" ? C.goldlt : "#fff" }}>
            <input type="radio" checked={payMode === "comptant"} onChange={() => setPayMode("comptant")} data-testid="p-pay-comptant" />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Carte bancaire · Comptant</div>
              <div style={{ fontSize: 11, color: C.mid }}>Paiement immédiat sécurisé Stripe</div>
            </div>
          </label>
        </div>
      </div>

      <div style={{ marginBottom: 20, background: cgvAccept ? "#F0FDF4" : "#fff", border: `1px solid ${cgvAccept ? C.green + "66" : C.border}`, borderRadius: 10, padding: 16 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={cgvAccept} onChange={(e) => setCgvAccept(e.target.checked)}
                 data-testid="p-cgv-checkbox"
                 style={{ marginTop: 3, width: 18, height: 18, accentColor: C.gold, cursor: "pointer", flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
            Je reconnais avoir pris connaissance et j'accepte les{" "}
            <button type="button" onClick={(e) => { e.preventDefault(); setShowModal("cgv"); }}
                    data-testid="p-cgv-link"
                    style={{ color: C.gold, textDecoration: "underline", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>
              Conditions Générales de Vente
            </button>{" "}et la{" "}
            <button type="button" onClick={(e) => { e.preventDefault(); setShowModal("rgpd"); }}
                    data-testid="p-rgpd-link"
                    style={{ color: C.gold, textDecoration: "underline", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>
              Politique de confidentialité (RGPD)
            </button>{" "}d'AUPRÉA. J'autorise le traitement de mes données personnelles aux fins de la prestation. Je peux à tout moment exercer mes droits via{" "}
            <button type="button" onClick={(e) => { e.preventDefault(); setShowModal("rgpd-demande"); }}
                    data-testid="p-rgpd-demande-link"
                    style={{ color: C.teal, textDecoration: "underline", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}>
              ce formulaire
            </button>.
          </div>
        </label>
      </div>

      {err && <div style={{ background: C.redlt, color: C.red, padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>⚠ {err}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 30 }}>
        <button onClick={pay} disabled={processing || !cgvAccept}
          data-testid="p-payer"
          style={{ background: cgvAccept ? C.gold : C.border, color: "#fff", border: "none", padding: "14px 30px", borderRadius: 10, cursor: (processing || !cgvAccept) ? "not-allowed" : "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 700, opacity: processing ? 0.6 : 1 }}>
          {processing ? "Redirection…" : `Payer ${fmtEur(total)}`}
        </button>
      </div>

      <Footer onOpen={setShowModal} />

      {showModal && <LegalModal type={showModal} texts={texts} onClose={() => setShowModal(null)} />}
    </Container>
  );
};

// ── Footer + Modals légaux ──────────────────────────────────────────────────
const Footer = ({ onOpen }) => (
  <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 30, paddingTop: 20, textAlign: "center", fontSize: 11, color: C.muted }} data-testid="public-footer">
    <div style={{ marginBottom: 6 }}>
      AUPRÉA SAS · SIRET 933 283 459 00016 · Paiement sécurisé par Stripe
    </div>
    <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
      <button type="button" onClick={() => onOpen("cgv")} data-testid="footer-cgv"
              style={{ color: C.mid, background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: 0 }}>Conditions Générales de Vente</button>
      <button type="button" onClick={() => onOpen("rgpd")} data-testid="footer-rgpd"
              style={{ color: C.mid, background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: 0 }}>Politique de confidentialité</button>
      <button type="button" onClick={() => onOpen("mentions")} data-testid="footer-mentions"
              style={{ color: C.mid, background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: 0 }}>Mentions légales</button>
      <button type="button" onClick={() => onOpen("rgpd-demande")} data-testid="footer-rgpd-demande"
              style={{ color: C.teal, background: "none", border: "none", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 11, padding: 0 }}>Exercer mes droits RGPD</button>
    </div>
  </div>
);

const ModalShell = ({ title, onClose, children, testid }) => (
  <div onClick={onClose} data-testid={testid} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 640, width: "100%", maxHeight: "88vh", overflow: "auto", padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{title}</div>
        <button onClick={onClose} data-testid="legal-close" style={{ background: "none", border: "none", fontSize: 24, color: C.mid, cursor: "pointer" }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const RgpdDemandeForm = ({ onDone }) => {
  const [form, setForm] = useState({ email: "", nom: "", type: "acces", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'ok' | 'err'
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!form.email || !form.message) { setErr("E-mail et message requis"); return; }
    setStatus("sending"); setErr("");
    try {
      await api.post("/public/rgpd/demande", form);
      setStatus("ok");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur"); setStatus("err");
    }
  };
  if (status === "ok") return (
    <div style={{ background: C.greenlt, color: C.green, padding: 16, borderRadius: 10, textAlign: "center" }} data-testid="rgpd-ok">
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Demande enregistrée ✓</div>
      <div style={{ fontSize: 12 }}>Notre DPO vous recontactera sous 30 jours maximum (contact : dpo@auprea.com).</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.5, marginBottom: 14 }}>
        Conformément au RGPD, vous disposez de droits sur vos données personnelles. Précisez votre demande ci-dessous : notre DPO vous répondra sous 30 jours maximum.
      </div>
      {err && <div style={{ background: C.redlt, color: C.red, padding: 10, borderRadius: 8, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "grid", gap: 10 }}>
        <input placeholder="Votre e-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
               data-testid="rgpd-email"
               style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
        <input placeholder="Nom / Prénom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
               style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", outline: "none" }} />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="rgpd-type"
                style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", background: "#fff", outline: "none" }}>
          <option value="acces">Droit d'accès à mes données</option>
          <option value="rectification">Rectification</option>
          <option value="effacement">Effacement (droit à l'oubli)</option>
          <option value="portabilite">Portabilité</option>
          <option value="opposition">Opposition au traitement</option>
          <option value="autre">Autre</option>
        </select>
        <textarea placeholder="Précisez votre demande…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="rgpd-message" rows={4}
                  style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical" }} />
        <button onClick={submit} disabled={status === "sending"}
                data-testid="rgpd-submit"
                style={{ background: C.gold, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {status === "sending" ? "Envoi…" : "Envoyer ma demande"}
        </button>
      </div>
    </div>
  );
};

const LegalModal = ({ type, texts, onClose }) => {
  const mentions = texts?.mentions_legales;
  if (type === "cgv") {
    return (
      <ModalShell title="Conditions Générales de Vente" onClose={onClose} testid="modal-cgv">
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }} data-testid="cgv-content">
          {texts?.cgv || "Les CGV n'ont pas pu être chargées."}
        </div>
      </ModalShell>
    );
  }
  if (type === "rgpd") {
    return (
      <ModalShell title="Politique de confidentialité (RGPD)" onClose={onClose} testid="modal-rgpd">
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: 20 }} data-testid="rgpd-content">
          {texts?.rgpd || "La politique de confidentialité n'a pas pu être chargée."}
        </div>
        <div style={{ fontSize: 12, color: C.mid, background: C.gray2, padding: 12, borderRadius: 8 }}>
          <strong>Vos droits :</strong> accès, rectification, effacement, portabilité, opposition.<br/>
          <strong>Délégué à la Protection des Données (DPO)</strong> : {mentions?.dpo || "dpo@auprea.com"}<br/>
          <strong>Base légale</strong> : exécution du contrat de prestation + consentement explicite.<br/>
          <strong>Durée de conservation</strong> : 10 ans à compter de la fin de la prestation (obligations légales et comptables).<br/>
          Vous pouvez à tout moment déposer une réclamation auprès de la CNIL (www.cnil.fr).
        </div>
      </ModalShell>
    );
  }
  if (type === "mentions") {
    return (
      <ModalShell title="Mentions légales" onClose={onClose} testid="modal-mentions">
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
          <div><strong>Éditeur :</strong> {mentions?.societe || "AUPRÉA SAS"}</div>
          <div><strong>SIRET :</strong> {mentions?.siret || "933 283 459 00016"}</div>
          <div><strong>Capital social :</strong> {mentions?.capital || "1 000 €"}</div>
          <div><strong>Siège social :</strong> {mentions?.siege || "—"}</div>
          <div><strong>Contact :</strong> <a href={`mailto:${mentions?.contact || "contact@auprea.com"}`} style={{ color: C.gold }}>{mentions?.contact || "contact@auprea.com"}</a></div>
          <div><strong>DPO :</strong> <a href={`mailto:${mentions?.dpo || "dpo@auprea.com"}`} style={{ color: C.gold }}>{mentions?.dpo || "dpo@auprea.com"}</a></div>
          <div style={{ marginTop: 10 }}><strong>Hébergeur :</strong> {mentions?.hebergeur || "—"}</div>
          <div style={{ marginTop: 10 }}><strong>Paiement :</strong> Stripe Payments Europe Limited (sécurisation SSL/PCI-DSS)</div>
        </div>
      </ModalShell>
    );
  }
  if (type === "rgpd-demande") {
    return (
      <ModalShell title="Exercer mes droits RGPD" onClose={onClose} testid="modal-rgpd-demande">
        <RgpdDemandeForm onDone={onClose} />
      </ModalShell>
    );
  }
  return null;
};

// ── Écran de succès + booking slot ─────────────────────────────────────────
const SuccessPage = ({ sessionId }) => {
  const [tx, setTx] = useState(null);
  const [status, setStatus] = useState("polling"); // polling / paid / failed
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booked, setBooked] = useState(null);
  const [err, setErr] = useState("");
  const [texts, setTexts] = useState(null);
  const [showModal, setShowModal] = useState(null);

  useEffect(() => {
    api.get(`/public/reglages-texts`).then(r => setTexts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setTx(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          // Fetch parcours info (public + conseillerId) via status → need conseillerId
          try {
            const parc = await api.get(`/public/parcours-by-id/${r.data.parcoursId}`);
            const consId = parc?.data?.conseillerId;
            if (consId) {
              const s = await api.get(`/disponibilites/${consId}`);
              setSlots(s.data);
            }
          } catch {}
        } else if (attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch (e) {
        if (attempts < 15) setTimeout(poll, 2000);
        else setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  const book = async () => {
    if (!selectedSlot || !tx) return;
    try {
      const r = await api.post("/public/book", { parcoursId: tx.parcoursId, slotId: selectedSlot, email: "" });
      setBooked(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };

  if (status === "polling") return <Container><div style={{ textAlign: "center", color: C.mid, marginTop: 100 }}>Confirmation de votre paiement…</div></Container>;
  if (status === "timeout") return <Container><div style={{ background: C.amberlt, color: C.amber, padding: 20, borderRadius: 10, marginTop: 40 }}>⏳ Le paiement est en cours de traitement. Vous recevrez un e-mail de confirmation dès qu'il sera validé.</div></Container>;

  return (
    <Container>
      <Header />
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 14, padding: 32, marginBottom: 30 }}>
        <div style={{ fontSize: 34, fontWeight: 700, color: C.green, fontFamily: "'Playfair Display', serif", marginBottom: 10 }}>Merci ! Votre paiement est confirmé ✓</div>
        <div style={{ fontSize: 15, color: C.mid, marginBottom: 20 }}>
          Un e-mail de confirmation vient de vous être envoyé avec votre facture.
        </div>
        <div style={{ background: C.goldlt, borderRadius: 8, padding: 16, fontSize: 13, color: C.golddm }}>
          <strong>Vos identifiants du carnet digital</strong> vous seront envoyés par e-mail dans les prochaines minutes (si vous en avez souscrit un).
        </div>
      </div>

      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 14, padding: 24, fontSize: 14, color: C.mid }} data-testid="p-rdv-consultant">
        Votre consultant vous contacte très prochainement pour convenir de votre rendez-vous. Vous recevrez une confirmation par e-mail.
      </div>
      <Footer onOpen={setShowModal} />
      {showModal && <LegalModal type={showModal} texts={texts} onClose={() => setShowModal(null)} />}
    </Container>
  );
};

// ── Router local ─────────────────────────────────────────────────────────────
export default function PublicRouter() {
  const path = window.location.pathname;
  // /p/:token or /p/paiement-succes?session_id=...
  const m = path.match(/^\/p\/(.+)$/);
  if (!m) return null;
  const seg = m[1];
  if (seg.startsWith("documents/")) return <DocumentsClient token={seg.slice(10)} />;
  if (seg.startsWith("rapport/")) return <RapportClient token={seg.slice(8)} />;
  if (seg.startsWith("dossier/")) return <DossierClient token={seg.slice(8)} />;
  if (seg.startsWith("atelier/")) return <QuestionnaireAtelier token={seg.slice(8)} />;
  if (seg.startsWith("courriers/")) return <CourriersFamille token={seg.slice(10)} />;
  if (seg === "paiement-succes") {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return <Container><div style={{ color: C.red, marginTop: 40 }}>Session ID manquant.</div></Container>;
    return <SuccessPage sessionId={sessionId} />;
  }
  if (new URLSearchParams(window.location.search).get("payer")) return <PaiementPage token={seg} fallback={<PublicClientPage token={seg} />} />;
  return <SignaturePage token={seg} fallback={<PublicClientPage token={seg} />} />;
}
